import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical } from "@/components/site/site-config";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { ResponsivePicture } from "@/components/site/images";
import { CASE_STUDIES } from "@/components/site/case-studies";

export const Route = createFileRoute("/projects/")({
  head: () => ({
    meta: [
      { title: "Roofing Projects in Citrus County | Destination Roofing" },
      { name: "description", content: "Real roofing project case studies from Destination Roofing Inc. — see before-and-after photographs and how each Citrus County, FL roof was evaluated." },
      { property: "og:title", content: "Roofing Projects in Citrus County | Destination Roofing" },
      { property: "og:description", content: "Real roofing project case studies with before-and-after photographs from Citrus County, FL." },
      { property: "og:url", content: canonical("/projects") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/projects") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbListJsonLd([{ label: "Roofing Projects", href: "/projects" }])),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Roofing Projects", href: "/projects" }]} />
      <PageHero
        eyebrow="Roofing Projects"
        title="Roofing Projects Across Citrus County"
        subtitle="Real Destination Roofing project case studies with genuine photographs, so homeowners can see the conditions we inspect and the work we complete."
      />
      <div className="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-2">
          {CASE_STUDIES.map((s) => {
            const cover = (s.after ?? s.during ?? s.before ?? [])[0];
            return (
              <article key={s.slug} className="border border-border rounded-lg overflow-hidden bg-card">
                {cover && (
                  <ResponsivePicture
                    image={cover.image}
                    alt={cover.alt}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    imgClassName="w-full h-64 object-cover"
                  />
                )}
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {s.city}, {s.state} · {s.roofType}{s.statusState === "in-progress" ? " · In progress" : ""}
                  </div>
                  <h2 className="mt-1 font-display text-xl font-bold">
                    <Link to={`/projects/${s.slug}` as string} className="hover:text-brand">{s.breadcrumbLabel}</Link>
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.summary}</p>
                  <Link to={`/projects/${s.slug}` as string} className="mt-3 inline-block font-semibold text-brand hover:underline">
                    View this roofing project →
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
        <p className="mt-10 text-muted-foreground">
          More real project photographs are in the <Link to="/gallery" className="text-brand hover:underline">Destination Roofing project gallery</Link>.
        </p>
      </div>
      <CTASection />
    </>
  );
}
